import { apiGet } from '@/lib/api';

export type PublicAppAvailability = 'available' | 'coming_soon';
export type PublicAppTier = 'included' | 'addon' | 'soon';

export interface PublicAppPricing {
  monthly_price_cents: number;
  included_usage: number | null;
  usage_unit: string | null;
  overage_unit_price_cents: number | null;
  trial_days: number;
}

/** Nível contratável de um app (ex.: Essencial e Completo). Só vem quando há mais de um. */
export interface PublicAppTierOption {
  code: string;
  name: string;
  description: string | null;
  monthly_price_cents: number;
  included_usage: number | null;
  overage_unit_price_cents: number | null;
  /** Chaves dos recursos do nível; o texto de cada uma está em `addon-tiers.ts`. */
  features: string[];
}

/** App vindo de `GET /api/public/apps` (platform_apps + platform_addons). */
export interface PublicApp {
  slug: string;
  name: string;
  description: string | null;
  /** Nome de ícone lucide (ex.: 'CalendarDays'). */
  icon: string | null;
  category: string;
  availability: PublicAppAvailability;
  tier: PublicAppTier;
  sort_order: number;
  /** Presente apenas quando `availability = 'available'`. */
  pricing: PublicAppPricing | null;
  /**
   * Código do add-on comprável quando houver billing_sku ativo.
   * `null` = não contratável via calculadora/checkout.
   */
  addon_code?: string | null;
  /**
   * Níveis do app, do mais simples ao mais completo. Ausente ou `null` = nível único.
   * Com níveis, `pricing` é o do mais barato e o checkout precisa enviar o nível escolhido.
   */
  tiers?: PublicAppTierOption[] | null;
}

/** Níveis do app só valem quando há mais de um para escolher. */
export function appTiers(app: PublicApp): PublicAppTierOption[] {
  return app.tiers && app.tiers.length > 1 ? app.tiers : [];
}

/** Nível pelo código; sem código válido, o mais barato (é o que o catálogo anuncia). */
export function tierOf(app: PublicApp, code?: string | null): PublicAppTierOption | null {
  const tiers = appTiers(app);
  if (tiers.length === 0) return null;
  return tiers.find((t) => t.code === code) ?? tiers.reduce((a, b) => (b.monthly_price_cents < a.monthly_price_cents ? b : a));
}

export function formatAppPrice(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function appBadgeLabel(tier: PublicAppTier): string {
  if (tier === 'included') return 'Incluso';
  if (tier === 'addon') return 'Add-on';
  return 'Em breve';
}

/** Rótulo principal de preço do card. `null` para "Em breve". */
export function appPriceLabel(app: PublicApp): string | null {
  if (app.tier === 'included') return 'Incluso no plano';
  if (app.tier === 'addon' && app.pricing && app.pricing.monthly_price_cents > 0) {
    const from = appTiers(app).length > 0 ? 'a partir de ' : '';
    return `${from}${formatAppPrice(app.pricing.monthly_price_cents)}/mês`;
  }
  return null;
}

/**
 * Unidade de uso no singular. O catálogo guarda no singular ('nota', 'contrato'), mas o Hub Comercial
 * chegou com 'pedidos': sem isto a página escrevia "1000 pedidoss/mês" e "R$ 0,08/pedidos".
 */
export function usageUnitSingular(unit: string): string {
  return /[^s]s$/i.test(unit) ? unit.slice(0, -1) : unit;
}

/** "1 pedido", "1000 pedidos": a unidade já no número certo, venha o catálogo no singular ou no plural. */
export function usageUnitFor(unit: string, quantity: number): string {
  const singular = usageUnitSingular(unit);
  return quantity === 1 ? singular : `${singular}s`;
}

/**
 * Nome com que o app é apresentado quando difere do nome curto do catálogo. O Hub Comercial se chama
 * "Operax Hub Comercial" na listagem da Amazon (Selling Partner Appstore), e a Amazon exige que o site
 * mostre o mesmo nome e que ele não seja só um termo genérico.
 */
const PUBLIC_NAME: Record<string, string> = { 'commercial-hub': 'Operax Hub Comercial' };

export function appPublicName(app: Pick<PublicApp, 'slug' | 'name'>): string {
  return PUBLIC_NAME[app.slug] ?? app.name;
}

/** Complemento do título de busca. O padrão é "app para WhatsApp"; o Hub é de marketplaces. */
const TITLE_KIND: Record<string, string> = { 'commercial-hub': 'estoque e pedidos de marketplaces' };

export function appTitleKind(app: Pick<PublicApp, 'slug'>): string {
  return TITLE_KIND[app.slug] ?? 'app para WhatsApp';
}

/** Linha secundária de franquia/excedente, quando houver. */
export function appUsageHint(app: PublicApp): string | null {
  const p = app.pricing;
  // Com níveis, a franquia e o excedente mudam de um para o outro: a comparação diz, a linha única enganaria.
  if (!p || appTiers(app).length > 0) return null;
  const parts: string[] = [];
  if (p.included_usage && p.included_usage > 0 && p.usage_unit) {
    parts.push(`inclui ${p.included_usage} ${usageUnitFor(p.usage_unit, p.included_usage)}/mês`);
  }
  if (p.overage_unit_price_cents && p.overage_unit_price_cents > 0 && p.usage_unit) {
    parts.push(`excedente ${formatAppPrice(p.overage_unit_price_cents)}/${usageUnitSingular(p.usage_unit)}`);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

export function sortApps(apps: PublicApp[]): PublicApp[] {
  return [...apps].sort(
    (a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name),
  );
}

let cachedApps: PublicApp[] | null = null;
let inflight: Promise<PublicApp[]> | null = null;

export function fetchPublicApps(): Promise<PublicApp[]> {
  if (cachedApps) return Promise.resolve(cachedApps);
  if (!inflight) {
    inflight = apiGet<PublicApp[]>('/api/public/apps')
      .then((data) => {
        cachedApps = data;
        return data;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

/** Invalida cache (ex.: após reload forçado). */
export function clearPublicAppsCache(): void {
  cachedApps = null;
}
